import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Box,
  Typography,
  Divider,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Avatar,
  Button,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { ChevronLeft, ChevronRight, DragIndicator, Reorder } from "@mui/icons-material";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import LanguageIcon from "@mui/icons-material/Language";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import DriveEtaIcon from "@mui/icons-material/DriveEta";

export const resumeMeta = {
  hasPhoto: true,
  columns: 2,
};

const pageSizeMap = {
  A4: { width: "210mm", height: "297mm" },
  Letter: { width: "216mm", height: "279mm" },
  Legal: { width: "216mm", height: "356mm" },
  A3: { width: "297mm", height: "420mm" },
  Executive: { width: "184mm", height: "267mm" },
};

const defaultLeftSectionOrder = [
  "photo",
  "nameTitle",
  "contact",
  "skills",
  "languages",
  "interests",
  "references",
];

const defaultRightSectionOrder = [
  "profile",
  "career",
  "education",
  "certifications",
  "accomplishments",
  "softwareSkills",
  "volunteering",
  "websites",
  "additionalInfo",
];

const overflowFixStyle = {
  overflow: "hidden",
  wordBreak: "break-word",
  overflowWrap: "break-word",
  whiteSpace: "normal",
  maxWidth: "100%",
};

const ResumeTemplate = ({
  theme = { primary: "#f4b400", dark: "#000" },
  font = "Arial",
  fontSize = "14px",
  fontStyle = "normal",
  headingSize = "24px",
  sectionSpacing = 10,
  paragraphSpacing = 10,
  lineSpacing = 20,
  topBottomMargin = 20,
  sideMargins = 20,
  paragraphIndent = 0,
  lineWeight = 1,
  pageSize = "A4",
  photo = "",
  formData = {},
  exporting = false,
  enableDragDrop = true,
}) => {
  const [themeColor, setThemeColor] = useState(
    typeof theme === "string" ? { primary: theme, dark: "#000" } : theme
  );

  const [educationEntries, setEducationEntries] = useState([]);
  const [savedSkills, setSavedSkills] = useState([]);
  const [savedSummary, setSavedSummary] = useState("");
  const [savedAdditionalInfo, setSavedAdditionalInfo] = useState([]);
  const [savedLanguages, setSavedLanguages] = useState([]);
  const [savedAccomplishments, setSavedAccomplishments] = useState([]);
  const [savedCertifications, setSavedCertifications] = useState([]);
  const [savedReferences, setSavedReferences] = useState([]);
  const [softwareSkills, setSoftwareSkills] = useState([]);
  const [savedVolunteering, setSavedVolunteering] = useState([]);
  const [savedInterests, setSavedInterests] = useState([]);
  const [savedWebsites, setSavedWebsites] = useState([]);
  const [customSections, setCustomSections] = useState({});
  const [workExperiences, setWorkExperiences] = useState([]);
  const [localFormData, setLocalFormData] = useState({});

  const [currentPage, setCurrentPage] = useState(0);
  const [pages, setPages] = useState([]);
  const [sidebarPages, setSidebarPages] = useState([]);
  const rightContentRef = useRef(null);
  const sidebarContentRef = useRef(null);

  const [leftSectionOrder, setLeftSectionOrder] = useState(defaultLeftSectionOrder);
  const [rightSectionOrder, setRightSectionOrder] = useState(defaultRightSectionOrder);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [dragOverSide, setDragOverSide] = useState(null);
  const [arrangeDialogOpen, setArrangeDialogOpen] = useState(false);

  const page = pageSizeMap[pageSize] || pageSizeMap.A4;

  const globalFontStyle = {
    fontFamily: font,
    fontSize: fontSize,
    fontStyle: fontStyle,
    lineHeight: `${lineSpacing}px`,
    ...overflowFixStyle,
  };

  const getTextStyle = (additionalStyles = {}) => ({
    ...globalFontStyle,
    ...additionalStyles,
  });

  useEffect(() => {
    setThemeColor(
      typeof theme === "string" ? { primary: theme, dark: "#000" } : theme
    );
  }, [theme]);

  // ─── Load all data from localStorage ─────────────────────────────────────
  useEffect(() => {
    const storedEntries = localStorage.getItem("educationEntries");
    if (storedEntries) setEducationEntries(JSON.parse(storedEntries));

    const storedSkills = localStorage.getItem("skills");
    if (storedSkills) setSavedSkills(JSON.parse(storedSkills));

    const storedSummaries = JSON.parse(localStorage.getItem("summaries")) || [];
    if (storedSummaries.length > 0)
      setSavedSummary(storedSummaries[storedSummaries.length - 1]);

    const storedAdditionalInfo =
      JSON.parse(localStorage.getItem("additionalInfo")) || [];
    setSavedAdditionalInfo(storedAdditionalInfo);

    const storedLanguages =
      JSON.parse(localStorage.getItem("languagesList")) || [];
    setSavedLanguages(storedLanguages);

    const storedAccomplishments =
      JSON.parse(localStorage.getItem("accomplishmentsList")) || [];
    setSavedAccomplishments(storedAccomplishments);

    const storedCertifications =
      JSON.parse(localStorage.getItem("certificationsList")) || [];
    setSavedCertifications(storedCertifications);

    const storedReferences =
      JSON.parse(localStorage.getItem("referencesList")) || [];
    setSavedReferences(storedReferences);

    const storedSoftwareSkills =
      JSON.parse(localStorage.getItem("softwareSkills")) || [];
    setSoftwareSkills(storedSoftwareSkills);

    const storedVolunteering =
      JSON.parse(localStorage.getItem("volunteering")) || [];
    setSavedVolunteering(storedVolunteering);

    const storedInterests =
      JSON.parse(localStorage.getItem("interests")) || [];
    setSavedInterests(storedInterests);

    const storedWebsites =
      JSON.parse(localStorage.getItem("websites")) || [];
    setSavedWebsites(storedWebsites);

    const storedWorkExperiences =
      JSON.parse(localStorage.getItem("workExperiences")) || [];
    setWorkExperiences(storedWorkExperiences);

    const storedFormData =
      JSON.parse(localStorage.getItem("formData")) || {};
    setLocalFormData(storedFormData);

    // Custom sections
    const userId = localStorage.getItem("userId");
    let sectionsData = {};
    if (userId) {
      const currentCustomSections =
        JSON.parse(
          localStorage.getItem(`current_custom_sections_${userId}`)
        ) || [];
      currentCustomSections.forEach((sectionName) => {
        const userCustomKey = `custom_${userId}_${sectionName}`;
        try {
          const customSectionData = localStorage.getItem(userCustomKey);
          if (customSectionData) {
            const parsedData = JSON.parse(customSectionData);
            const items = (parsedData.items || []).filter(
              (item) => item && item.trim() !== ""
            );
            if (items.length > 0) sectionsData[sectionName] = items;
          }
        } catch (error) {
          console.error(`Error loading custom section ${sectionName}:`, error);
        }
      });
    }
    setCustomSections(sectionsData);

    // Section order
    const savedLeft = localStorage.getItem("resumeTemplate8LeftSectionOrder");
    if (savedLeft) {
      const parsed = JSON.parse(savedLeft).filter(
        (s) => !s.startsWith("custom_")
      );
      setLeftSectionOrder(parsed);
    } else {
      setLeftSectionOrder(defaultLeftSectionOrder);
    }

    const savedRight = localStorage.getItem("resumeTemplate8RightSectionOrder");
    if (savedRight) {
      const parsed = JSON.parse(savedRight).filter(
        (s) => !s.startsWith("custom_")
      );
      const customIds = Object.keys(sectionsData).map((n) => `custom_${n}`);
      setRightSectionOrder([...parsed, ...customIds]);
    } else {
      const customIds = Object.keys(sectionsData).map((n) => `custom_${n}`);
      setRightSectionOrder([...defaultRightSectionOrder, ...customIds]);
    }
  }, []);

  // Sync custom sections into order arrays
  useEffect(() => {
    const userId = localStorage.getItem("userId");
    if (!userId) return;
    const customIds = Object.keys(customSections).map((n) => `custom_${n}`);
    const filteredRight = rightSectionOrder.filter(
      (s) => !s.startsWith("custom_")
    );
    const newRight = [...filteredRight, ...customIds];
    if (JSON.stringify(newRight) !== JSON.stringify(rightSectionOrder)) {
      setRightSectionOrder(newRight);
      localStorage.setItem(
        "resumeTemplate8RightSectionOrder",
        JSON.stringify(newRight)
      );
    }
    const filteredLeft = leftSectionOrder.filter(
      (s) => !s.startsWith("custom_")
    );
    if (JSON.stringify(filteredLeft) !== JSON.stringify(leftSectionOrder)) {
      setLeftSectionOrder(filteredLeft);
      localStorage.setItem(
        "resumeTemplate8LeftSectionOrder",
        JSON.stringify(filteredLeft)
      );
    }
  }, [customSections]);

  useEffect(() => {
    if (leftSectionOrder.length > 0)
      localStorage.setItem(
        "resumeTemplate8LeftSectionOrder",
        JSON.stringify(leftSectionOrder)
      );
  }, [leftSectionOrder]);

  useEffect(() => {
    if (rightSectionOrder.length > 0)
      localStorage.setItem(
        "resumeTemplate8RightSectionOrder",
        JSON.stringify(rightSectionOrder)
      );
  }, [rightSectionOrder]);

  // ─── Default / merged data ────────────────────────────────────────────────
  const defaultData = {
    firstName: "Alan",
    lastName: "Brown",
    currentPosition: "Job Title",
    phone: "+123-456-7890",
    email: "hello@email.com",
    city: "123, Main Street, Birmingham",
    website: "linkedin.com/in/alanbrown",
    linkedIn: "",
    drivingLicence: "",
    profile:
      "Write a short brief introduction of just a few short paragraphs explaining your key skills, career background, and what you're looking for. Keep it professional and simple.",
  };

  const data = { ...defaultData, ...localFormData, ...formData };

  const formatDate = (month, year) => {
    if (!month || !year) return "";
    return `${month} ${year}`;
  };

  // ─── Drag & Drop Handlers ─────────────────────────────────────────────────
  const handleLeftDragStart = (e, index) => {
    e.dataTransfer.setData(
      "text/plain",
      JSON.stringify({
        index,
        side: "left",
        sectionId: leftSectionOrder[index],
      })
    );
    e.currentTarget.style.opacity = "0.5";
  };

  const handleRightDragStart = (e, index) => {
    e.dataTransfer.setData(
      "text/plain",
      JSON.stringify({
        index,
        side: "right",
        sectionId: rightSectionOrder[index],
      })
    );
    e.currentTarget.style.opacity = "0.5";
  };

  const handleDragOver = (e, index, side) => {
    e.preventDefault();
    setDragOverIndex(index);
    setDragOverSide(side);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOverIndex(null);
    setDragOverSide(null);
  };

  const handleLeftDrop = (e, targetIndex) => {
    e.preventDefault();
    const dropData = JSON.parse(e.dataTransfer.getData("text/plain"));
    const { index: sourceIndex, side: sourceSide, sectionId } = dropData;
    let newLeft = [...leftSectionOrder];
    let newRight = [...rightSectionOrder];
    if (sourceSide === "left") {
      newLeft.splice(sourceIndex, 1);
    } else {
      newRight.splice(sourceIndex, 1);
    }
    newLeft.splice(targetIndex, 0, sectionId);
    setLeftSectionOrder(newLeft);
    setRightSectionOrder(newRight);
    setDragOverIndex(null);
    setDragOverSide(null);
    e.currentTarget.style.opacity = "1";
  };

  const handleRightDrop = (e, targetIndex) => {
    e.preventDefault();
    const dropData = JSON.parse(e.dataTransfer.getData("text/plain"));
    const { index: sourceIndex, side: sourceSide, sectionId } = dropData;
    let newLeft = [...leftSectionOrder];
    let newRight = [...rightSectionOrder];
    if (sourceSide === "right") {
      newRight.splice(sourceIndex, 1);
    } else {
      newLeft.splice(sourceIndex, 1);
    }
    newRight.splice(targetIndex, 0, sectionId);
    setLeftSectionOrder(newLeft);
    setRightSectionOrder(newRight);
    setDragOverIndex(null);
    setDragOverSide(null);
    e.currentTarget.style.opacity = "1";
  };

  const handleDragEnd = (e) => {
    setDragOverIndex(null);
    setDragOverSide(null);
    e.currentTarget.style.opacity = "1";
  };

  const resetLeftSectionOrder = () => setLeftSectionOrder(defaultLeftSectionOrder);
  const resetRightSectionOrder = () => {
    const customIds = Object.keys(customSections).map((n) => `custom_${n}`);
    setRightSectionOrder([...defaultRightSectionOrder, ...customIds]);
  };
  const resetAllSectionOrder = () => {
    resetLeftSectionOrder();
    resetRightSectionOrder();
  };

  const getSectionTitle = (sectionId) => {
    const titles = {
      photo: "Profile Photo",
      nameTitle: "Name & Title",
      contact: "Contact",
      skills: "Professional Skills",
      languages: "Languages",
      interests: "Interests",
      references: "References",
      profile: "Profile",
      career: "Career/Work Experience",
      education: "Education",
      certifications: "Certifications",
      accomplishments: "Accomplishments",
      softwareSkills: "Software Skills",
      volunteering: "Volunteering",
      websites: "Websites & Profiles",
      additionalInfo: "Additional Information",
    };
    if (sectionId.startsWith("custom_")) {
      const name = sectionId.replace("custom_", "").replace(/_/g, " ");
      return name.charAt(0).toUpperCase() + name.slice(1);
    }
    return titles[sectionId] || sectionId;
  };

  // ─── Contact Section (matches Resume30 logic exactly) ────────────────────
  const renderContactSection = (inSidebar = true) => {
    const hasAny =
      data.phone ||
      data.email ||
      data.city ||
      data.website ||
      data.linkedIn ||
      data.drivingLicence;

    if (!hasAny) return null;

    const iconStyle = {
      fontSize: 16,
      flexShrink: 0,
      mt: "1px",
    };

    const rowSx = {
      display: "flex",
      alignItems: "flex-start",
      gap: 1,
      mb: `${Math.max(4, paragraphSpacing / 3)}px`,
      width: "100%",
      ...overflowFixStyle,
    };

    const textSx = {
      ...globalFontStyle,
      wordBreak: "break-all",
    };

    return (
      <Box sx={{ mt: `${sectionSpacing}px`, ...overflowFixStyle }}>
        <Typography
          variant="h6"
          sx={getTextStyle({ fontWeight: "bold", fontSize: headingSize })}
        >
          CONTACT
        </Typography>
        <Box sx={{ mt: 1 }}>
          {data.phone && (
            <Box sx={rowSx}>
              <PhoneIcon sx={iconStyle} />
              <Typography sx={textSx}>{data.phone}</Typography>
            </Box>
          )}
          {data.email && (
            <Box sx={rowSx}>
              <EmailIcon sx={iconStyle} />
              <Typography sx={textSx}>{data.email}</Typography>
            </Box>
          )}
          {data.city && (
            <Box sx={rowSx}>
              <LocationOnIcon sx={iconStyle} />
              <Typography sx={textSx}>{data.city}</Typography>
            </Box>
          )}
          {data.website && (
            <Box sx={rowSx}>
              <LanguageIcon sx={iconStyle} />
              <Typography sx={textSx}>
                <a
                  href={
                    data.website.startsWith("http")
                      ? data.website
                      : `https://${data.website}`
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: "inherit",
                    textDecoration: "none",
                    wordBreak: "break-all",
                    fontFamily: font,
                    fontSize: fontSize,
                  }}
                >
                  {data.website.replace(/^https?:\/\/(www\.)?/, "")}
                </a>
              </Typography>
            </Box>
          )}
          {data.linkedIn && (
            <Box sx={rowSx}>
              <LinkedInIcon sx={{ ...iconStyle, color: "#0077b5" }} />
              <Typography sx={textSx}>
                <a
                  href={
                    data.linkedIn.startsWith("http")
                      ? data.linkedIn
                      : `https://${data.linkedIn}`
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: "#0077b5",
                    textDecoration: "none",
                    wordBreak: "break-all",
                    fontFamily: font,
                    fontSize: fontSize,
                  }}
                >
                  {data.linkedIn.replace(
                    /^https?:\/\/(www\.)?linkedin\.com\/in\/|linkedin\.com\/in\//,
                    ""
                  )}
                </a>
              </Typography>
            </Box>
          )}
          {data.drivingLicence && (
            <Box sx={rowSx}>
              <DriveEtaIcon sx={{ ...iconStyle, color: "#4caf50" }} />
              <Typography sx={textSx}>DL: {data.drivingLicence}</Typography>
            </Box>
          )}
        </Box>
      </Box>
    );
  };

  // ─── Sidebar Sections Renderer ────────────────────────────────────────────
  const renderSidebarSections = () => {
    return leftSectionOrder
      .map((sectionId) => {
        switch (sectionId) {
          case "photo":
            return resumeMeta.hasPhoto ? (
              <div key="photo" className="sidebar-section">
                <Box textAlign="center" mb={2}>
                  <Avatar
                    src={photo || "/profile.jpg"}
                    alt="profile"
                    sx={{ width: 90, height: 90, mx: "auto", mb: 1 }}
                  />
                </Box>
              </div>
            ) : null;

          case "nameTitle":
            return (
              <div key="nameTitle" className="sidebar-section">
                <Typography
                  fontWeight="bold"
                  sx={{
                    fontSize: headingSize,
                    fontFamily: font,
                    fontStyle: fontStyle,
                    lineHeight: `${lineSpacing}px`,
                    ...overflowFixStyle,
                  }}
                >
                  {data.firstName.toUpperCase()} {data.lastName.toUpperCase()}
                </Typography>
                <Typography
                  sx={{
                    mt: 1,
                    bgcolor: "black",
                    color: "white",
                    textAlign: "center",
                    p: 1,
                    fontFamily: font,
                    fontSize: fontSize,
                    fontStyle: fontStyle,
                    lineHeight: `${lineSpacing}px`,
                    ...overflowFixStyle,
                  }}
                >
                  {data.currentPosition.toUpperCase()}
                </Typography>
              </div>
            );

          case "contact":
            return (
              <div key="contact" className="sidebar-section">
                {renderContactSection(true)}
              </div>
            );

          case "skills":
            return savedSkills.length > 0 ? (
              <div key="skills" className="sidebar-section">
                <Box sx={{ mt: `${sectionSpacing}px`, ...overflowFixStyle }}>
                  <Typography
                    variant="h6"
                    sx={getTextStyle({
                      fontWeight: "bold",
                      fontSize: headingSize,
                    })}
                  >
                    PRO. SKILLS
                  </Typography>
                  <List disablePadding>
                    {savedSkills.map((skill, i) => (
                      <ListItem key={i} disableGutters dense>
                        <ListItemText
                          primary={skill.name}
                          primaryTypographyProps={getTextStyle()}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              </div>
            ) : null;

          case "languages":
            return savedLanguages.length > 0 ? (
              <div key="languages" className="sidebar-section">
                <Box sx={{ mt: `${sectionSpacing}px`, ...overflowFixStyle }}>
                  <Typography
                    variant="h6"
                    sx={getTextStyle({
                      fontWeight: "bold",
                      fontSize: headingSize,
                    })}
                  >
                    LANGUAGES
                  </Typography>
                  <List disablePadding>
                    {savedLanguages.map((lang, i) => (
                      <ListItem key={i} disableGutters dense>
                        <ListItemText
                          primary={`${lang.name}${lang.level ? ` - ${lang.level}` : ""}`}
                          primaryTypographyProps={getTextStyle()}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              </div>
            ) : null;

          case "interests":
            return savedInterests.length > 0 ? (
              <div key="interests" className="sidebar-section">
                <Box sx={{ mt: `${sectionSpacing}px`, ...overflowFixStyle }}>
                  <Typography
                    variant="h6"
                    sx={getTextStyle({
                      fontWeight: "bold",
                      fontSize: headingSize,
                    })}
                  >
                    INTERESTS
                  </Typography>
                  <List disablePadding>
                    {savedInterests.map((interest, i) => (
                      <ListItem key={i} disableGutters dense>
                        <ListItemText
                          primary={interest}
                          primaryTypographyProps={getTextStyle()}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              </div>
            ) : null;

          case "references":
            return (
              <div key="references" className="sidebar-section">
                <Box sx={{ mt: `${sectionSpacing}px`, ...overflowFixStyle }}>
                  <Typography
                    variant="h6"
                    sx={getTextStyle({
                      fontWeight: "bold",
                      fontSize: headingSize,
                    })}
                  >
                    REFERENCES
                  </Typography>
                  {savedReferences.length > 0 ? (
                    savedReferences.map((ref, i) => (
                      <Typography key={i} variant="body2" sx={getTextStyle()}>
                        {ref}
                      </Typography>
                    ))
                  ) : (
                    <Typography variant="body2" sx={getTextStyle()}>
                      Available on request
                    </Typography>
                  )}
                </Box>
              </div>
            );

          default:
            // Right sections moved to left — render them inline
            return renderRightSectionById(sectionId, true);
        }
      })
      .filter(Boolean);
  };

  // ─── Right Section by ID (supports both right panel and cross-side dragging) ──
  const renderRightSectionById = (currentSectionId, inSidebar = false) => {
    const className = inSidebar ? "sidebar-section" : "resume-section";
    const bgColor = inSidebar ? "transparent" : "black";
    const headerColor = inSidebar ? "inherit" : "white";

    const sectionHeaderSx = {
      bgcolor: bgColor,
      color: headerColor,
      p: 1,
      pl: 2,
      fontSize: headingSize,
      fontFamily: font,
      fontStyle: fontStyle,
      lineHeight: `${lineSpacing}px`,
      borderLeft: inSidebar
        ? "none"
        : `${lineWeight * 3}px solid ${themeColor.primary}`,
      ...overflowFixStyle,
    };

    const contentTextSx = {
      fontFamily: font,
      fontSize: fontSize,
      fontStyle: fontStyle,
      lineHeight: `${lineSpacing}px`,
      ...overflowFixStyle,
    };

    switch (currentSectionId) {
      case "profile":
        return (
          <div
            key="profile"
            className={className}
            style={{ marginBottom: `${sectionSpacing}px` }}
          >
            <Typography variant="h6" sx={sectionHeaderSx}>
              PROFILE
            </Typography>
            <Typography
              variant="body2"
              sx={{
                mt: 1,
                textIndent: `${paragraphIndent}px`,
                fontWeight: lineWeight,
                mb: `${paragraphSpacing}px`,
                ...contentTextSx,
              }}
            >
              {savedSummary || data.profile}
            </Typography>
          </div>
        );

      case "career":
        return workExperiences.length > 0 ? (
          <div
            key="career"
            className={className}
            style={{ marginBottom: `${sectionSpacing}px` }}
          >
            <Typography variant="h6" sx={sectionHeaderSx}>
              CAREER
            </Typography>
            {workExperiences.map((job, i) => (
              <Box
                key={i}
                sx={{ mt: 2, mb: `${paragraphSpacing}px`, ...overflowFixStyle }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    flexWrap: "wrap",
                    gap: 1,
                    ...overflowFixStyle,
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    fontWeight="bold"
                    sx={{ ...contentTextSx, flex: "1 1 auto", minWidth: 0 }}
                  >
                    {job.employer} — {job.jobTitle}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      ...contentTextSx,
                      flexShrink: 0,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {formatDate(job.startMonth, job.startYear)} -{" "}
                    {job.current
                      ? "Present"
                      : formatDate(job.endMonth, job.endYear)}
                  </Typography>
                </Box>
                {job.description && (
                  <Box
                    sx={{
                      mt: 1,
                      ...overflowFixStyle,
                      "& *": {
                        fontFamily: font,
                        fontSize: fontSize,
                        fontStyle: fontStyle,
                        lineHeight: `${lineSpacing}px`,
                        ...overflowFixStyle,
                      },
                      ...globalFontStyle,
                    }}
                    dangerouslySetInnerHTML={{
                      __html: job.description
                        .replace(
                          /\*\*(.*?)\*\*/g,
                          `<strong style="font-family:${font};font-size:${fontSize};font-style:${fontStyle};line-height:${lineSpacing}px;word-break:break-word;">$1</strong>`
                        )
                        .replace(
                          /\*(.*?)\*/g,
                          `<em style="font-family:${font};font-size:${fontSize};line-height:${lineSpacing}px;word-break:break-word;">$1</em>`
                        )
                        .replace(
                          /<u>(.*?)<\/u>/g,
                          `<u style="font-family:${font};font-size:${fontSize};font-style:${fontStyle};line-height:${lineSpacing}px;word-break:break-word;">$1</u>`
                        )
                        .replace(/\n/g, "<br/>"),
                    }}
                  />
                )}
              </Box>
            ))}
          </div>
        ) : null;

      case "education":
        return educationEntries.length > 0 ? (
          <div
            key="education"
            className={className}
            style={{ marginBottom: `${sectionSpacing}px` }}
          >
            <Typography variant="h6" sx={sectionHeaderSx}>
              EDUCATION
            </Typography>
            {educationEntries.map((edu, i) => {
              const schoolName =
                edu.educationLevel === "school"
                  ? edu.schoolName
                  : edu.collegeName;
              const degree =
                edu.educationLevel === "school"
                  ? edu.standard
                  : edu.degreeName;
              const fieldOfStudy =
                edu.educationLevel === "school"
                  ? edu.stream
                  : edu.specialization;
              let additionalCoursework = "";
              if (edu.educationLevel === "school") {
                additionalCoursework = `${edu.schoolLocation || ""}${edu.district ? `, ${edu.district}` : ""}${edu.percentage ? ` | ${edu.percentage}%` : ""}`;
              } else {
                additionalCoursework = `${edu.collegeLocation || ""}${edu.specialization ? ` | ${edu.specialization}` : ""}${edu.percentage ? ` | ${edu.percentage}%` : ""}${edu.cgpa ? ` | CGPA: ${edu.cgpa}` : ""}${edu.grade ? ` | Grade: ${edu.grade}` : ""}`;
              }
              return (
                <Box
                  key={i}
                  sx={{
                    mt: 1,
                    mb: `${paragraphSpacing}px`,
                    ...overflowFixStyle,
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{
                      textIndent: `${paragraphIndent}px`,
                      fontWeight: lineWeight,
                      ...contentTextSx,
                    }}
                  >
                    {formatDate(edu.gradMonth, edu.gradYear)}
                  </Typography>
                  <Typography variant="body2" fontWeight="bold" sx={contentTextSx}>
                    {degree} {fieldOfStudy && `- ${fieldOfStudy}`}
                  </Typography>
                  <Typography variant="body2" sx={contentTextSx}>
                    {schoolName}
                  </Typography>
                  {additionalCoursework && additionalCoursework.trim() && (
                    <Typography variant="body2" sx={contentTextSx}>
                      {additionalCoursework}
                    </Typography>
                  )}
                </Box>
              );
            })}
          </div>
        ) : null;

      case "certifications":
        return savedCertifications.length > 0 ? (
          <div
            key="certifications"
            className={className}
            style={{ marginBottom: `${sectionSpacing}px` }}
          >
            <Typography variant="h6" sx={sectionHeaderSx}>
              CERTIFICATIONS
            </Typography>
            {savedCertifications.map((cert, i) => (
              <Box
                key={i}
                sx={{
                  mt: 1,
                  mb: `${paragraphSpacing}px`,
                  ...overflowFixStyle,
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{
                    textIndent: `${paragraphIndent}px`,
                    fontWeight: lineWeight,
                    ...contentTextSx,
                  }}
                >
                  {cert.name}
                </Typography>
                <Typography variant="body2" sx={contentTextSx}>
                  {cert.provider} {cert.year && `(${cert.year})`}
                </Typography>
              </Box>
            ))}
          </div>
        ) : null;

      case "accomplishments":
        return savedAccomplishments.length > 0 ? (
          <div
            key="accomplishments"
            className={className}
            style={{ marginBottom: `${sectionSpacing}px` }}
          >
            <Typography variant="h6" sx={sectionHeaderSx}>
              ACCOMPLISHMENTS
            </Typography>
            <List dense disablePadding>
              {savedAccomplishments.map((acc, i) => (
                <ListItem key={i} sx={{ py: 0 }}>
                  <ListItemText
                    primary={acc}
                    primaryTypographyProps={contentTextSx}
                  />
                </ListItem>
              ))}
            </List>
          </div>
        ) : null;

      case "softwareSkills":
        return softwareSkills.length > 0 ? (
          <div
            key="softwareSkills"
            className={className}
            style={{ marginBottom: `${sectionSpacing}px` }}
          >
            <Typography variant="h6" sx={sectionHeaderSx}>
              SOFTWARE SKILLS
            </Typography>
            <List dense disablePadding>
              {softwareSkills.map((skill, i) => (
                <ListItem key={i} sx={{ py: 0 }}>
                  <ListItemText
                    primary={`${skill.name} - ${skill.level}%`}
                    primaryTypographyProps={contentTextSx}
                  />
                </ListItem>
              ))}
            </List>
          </div>
        ) : null;

      case "volunteering":
        return savedVolunteering.length > 0 ? (
          <div
            key="volunteering"
            className={className}
            style={{ marginBottom: `${sectionSpacing}px` }}
          >
            <Typography variant="h6" sx={sectionHeaderSx}>
              VOLUNTEERING
            </Typography>
            {savedVolunteering.map((vol, i) => (
              <Box
                key={i}
                sx={{
                  mt: 2,
                  mb: `${paragraphSpacing}px`,
                  ...overflowFixStyle,
                }}
              >
                <Typography fontWeight="bold" sx={contentTextSx}>
                  {vol.subtopic}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={contentTextSx}
                >
                  {vol.fromDate} - {vol.toDate}
                </Typography>
                <Typography sx={{ mt: 1, ...contentTextSx }}>
                  {vol.content}
                </Typography>
              </Box>
            ))}
          </div>
        ) : null;

      case "websites":
        return savedWebsites.length > 0 ? (
          <div
            key="websites"
            className={className}
            style={{ marginBottom: `${sectionSpacing}px` }}
          >
            <Typography variant="h6" sx={sectionHeaderSx}>
              WEBSITES & PROFILES
            </Typography>
            <List dense disablePadding>
              {savedWebsites.map((site, i) => (
                <ListItem key={i} sx={{ py: 0 }}>
                  <ListItemText
                    primary={
                      <a
                        href={
                          site.url.startsWith("http")
                            ? site.url
                            : `https://${site.url}`
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          color: "inherit",
                          textDecoration: "none",
                          fontFamily: font,
                          fontSize: fontSize,
                          wordBreak: "break-all",
                        }}
                      >
                        {site.name || site.url}
                      </a>
                    }
                    primaryTypographyProps={contentTextSx}
                  />
                </ListItem>
              ))}
            </List>
          </div>
        ) : null;

      case "additionalInfo":
        return savedAdditionalInfo.length > 0 ? (
          <div
            key="additionalInfo"
            className={className}
            style={{ marginBottom: `${sectionSpacing}px` }}
          >
            <Typography variant="h6" sx={sectionHeaderSx}>
              ADDITIONAL INFORMATION
            </Typography>
            <List dense disablePadding>
              {savedAdditionalInfo.map((info, i) => (
                <ListItem key={i} sx={{ py: 0 }}>
                  <ListItemText
                    primary={info}
                    primaryTypographyProps={contentTextSx}
                  />
                </ListItem>
              ))}
            </List>
          </div>
        ) : null;

      // Left sections moved to right — render them
      case "photo":
        return !inSidebar ? (
          <div
            key="photo"
            className={className}
            style={{ marginBottom: `${sectionSpacing}px` }}
          >
            <Box textAlign="center" mb={3}>
              <Avatar
                src={photo || "/profile.jpg"}
                alt="profile"
                sx={{ width: 100, height: 100, mx: "auto", mb: 2 }}
              />
            </Box>
          </div>
        ) : null;

      case "contact":
        return !inSidebar ? (
          <div
            key="contact"
            className={className}
            style={{ marginBottom: `${sectionSpacing}px` }}
          >
            {renderContactSection(false)}
          </div>
        ) : null;

      case "skills":
        return savedSkills.length > 0 && !inSidebar ? (
          <div
            key="skills"
            className={className}
            style={{ marginBottom: `${sectionSpacing}px` }}
          >
            <Box sx={{ mt: `${sectionSpacing}px`, ...overflowFixStyle }}>
              <Typography
                variant="h6"
                sx={getTextStyle({ fontWeight: "bold", fontSize: headingSize })}
              >
                PRO. SKILLS
              </Typography>
              <List disablePadding>
                {savedSkills.map((skill, i) => (
                  <ListItem key={i} disableGutters dense>
                    <ListItemText
                      primary={skill.name}
                      primaryTypographyProps={getTextStyle()}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          </div>
        ) : null;

      case "languages":
        return savedLanguages.length > 0 && !inSidebar ? (
          <div
            key="languages"
            className={className}
            style={{ marginBottom: `${sectionSpacing}px` }}
          >
            <Box sx={{ mt: `${sectionSpacing}px`, ...overflowFixStyle }}>
              <Typography
                variant="h6"
                sx={getTextStyle({ fontWeight: "bold", fontSize: headingSize })}
              >
                LANGUAGES
              </Typography>
              <List disablePadding>
                {savedLanguages.map((lang, i) => (
                  <ListItem key={i} disableGutters dense>
                    <ListItemText
                      primary={`${lang.name}${lang.level ? ` - ${lang.level}` : ""}`}
                      primaryTypographyProps={getTextStyle()}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          </div>
        ) : null;

      case "interests":
        return savedInterests.length > 0 && !inSidebar ? (
          <div
            key="interests"
            className={className}
            style={{ marginBottom: `${sectionSpacing}px` }}
          >
            <Box sx={{ mt: `${sectionSpacing}px`, ...overflowFixStyle }}>
              <Typography
                variant="h6"
                sx={getTextStyle({ fontWeight: "bold", fontSize: headingSize })}
              >
                INTERESTS
              </Typography>
              <List disablePadding>
                {savedInterests.map((interest, i) => (
                  <ListItem key={i} disableGutters dense>
                    <ListItemText
                      primary={interest}
                      primaryTypographyProps={getTextStyle()}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          </div>
        ) : null;

      case "references":
        return !inSidebar ? (
          <div
            key="references"
            className={className}
            style={{ marginBottom: `${sectionSpacing}px` }}
          >
            <Box sx={{ mt: `${sectionSpacing}px`, ...overflowFixStyle }}>
              <Typography
                variant="h6"
                sx={getTextStyle({ fontWeight: "bold", fontSize: headingSize })}
              >
                REFERENCES
              </Typography>
              {savedReferences.length > 0 ? (
                savedReferences.map((ref, i) => (
                  <Typography key={i} variant="body2" sx={getTextStyle()}>
                    {ref}
                  </Typography>
                ))
              ) : (
                <Typography variant="body2" sx={getTextStyle()}>
                  Available on request
                </Typography>
              )}
            </Box>
          </div>
        ) : null;

      default:
        if (currentSectionId && currentSectionId.startsWith("custom_")) {
          const customSectionName = currentSectionId.replace("custom_", "");
          const items = customSections[customSectionName] || [];
          const validItems = items.filter((item) => item && item.trim() !== "");
          if (validItems.length > 0) {
            return (
              <div
                key={currentSectionId}
                className={className}
                style={{ marginBottom: `${sectionSpacing}px` }}
              >
                <Typography variant="h6" sx={sectionHeaderSx}>
                  {customSectionName.replace(/_/g, " ").toUpperCase()}
                </Typography>
                <List dense disablePadding>
                  {validItems.map((item, i) => (
                    <ListItem key={i} sx={{ py: 0 }}>
                      <ListItemText
                        primary={`• ${item}`}
                        primaryTypographyProps={{
                          ...contentTextSx,
                          whiteSpace: "pre-line",
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              </div>
            );
          }
        }
        return null;
    }
  };

  // ─── Right Content Renderer ───────────────────────────────────────────────
  const renderRightContentSections = () => {
    return rightSectionOrder
      .map((sectionId) => renderRightSectionById(sectionId, false))
      .filter(Boolean);
  };

  // ─── Drag Panel for Dialog ────────────────────────────────────────────────
  const renderDragPanel = (side, sections) => (
    <Box sx={{ mb: 3 }}>
      <Typography
        variant="h6"
        sx={{ mb: 2, display: "flex", alignItems: "center" }}
      >
        <DragIndicator sx={{ mr: 1 }} />
        {side === "left" ? "Left Sidebar" : "Right Content"} Sections
        <Typography
          variant="body2"
          color="textSecondary"
          sx={{ ml: 1 }}
        >
          ({sections.length} sections)
        </Typography>
      </Typography>
      <Box
        sx={{
          maxHeight: 400,
          overflow: "auto",
          border: "1px solid #e0e0e0",
          borderRadius: 1,
          p: 1,
        }}
      >
        {sections.length > 0 ? (
          sections.map((sectionId, index) => (
            <Paper
              key={sectionId}
              draggable
              onDragStart={(e) =>
                side === "left"
                  ? handleLeftDragStart(e, index)
                  : handleRightDragStart(e, index)
              }
              onDragOver={(e) => handleDragOver(e, index, side)}
              onDragLeave={handleDragLeave}
              onDrop={(e) =>
                side === "left"
                  ? handleLeftDrop(e, index)
                  : handleRightDrop(e, index)
              }
              onDragEnd={handleDragEnd}
              sx={{
                p: 2,
                mb: 1,
                display: "flex",
                alignItems: "center",
                backgroundColor:
                  dragOverIndex === index && dragOverSide === side
                    ? "#f0f0f0"
                    : "white",
                boxShadow:
                  dragOverIndex === index && dragOverSide === side ? 2 : 1,
                cursor: "grab",
                "&:active": { cursor: "grabbing" },
                transition: "all 0.2s ease",
              }}
            >
              <DragIndicator color="action" />
              <Typography sx={{ ml: 1, flexGrow: 1 }}>
                {getSectionTitle(sectionId)}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {index + 1}
              </Typography>
            </Paper>
          ))
        ) : (
          <Typography
            variant="body2"
            color="textSecondary"
            sx={{ p: 2, textAlign: "center" }}
          >
            No sections. Drag sections from the other side.
          </Typography>
        )}
      </Box>
      <Button
        onClick={
          side === "left" ? resetLeftSectionOrder : resetRightSectionOrder
        }
        variant="outlined"
        size="small"
        sx={{ mt: 1 }}
      >
        Reset {side === "left" ? "Left" : "Right"} Order
      </Button>
    </Box>
  );

  // ─── Pagination ───────────────────────────────────────────────────────────
  const paginateSidebarContent = useCallback(() => {
    if (!sidebarContentRef.current) return;
    const pageHeightInPx =
      parseFloat(page.height.replace("mm", "")) * 3.78;
    const availableHeight = pageHeightInPx - 2 * topBottomMargin;
    const sections = Array.from(
      sidebarContentRef.current.querySelectorAll(".sidebar-section")
    );
    let newSidebarPages = [],
      currentPageSections = [],
      currentHeight = 0;
    sections.forEach((section, index) => {
      const sectionHeight = section.offsetHeight;
      if (currentHeight + sectionHeight > availableHeight) {
        if (currentPageSections.length > 0)
          newSidebarPages.push([...currentPageSections]);
        currentPageSections = [section.outerHTML];
        currentHeight = sectionHeight;
      } else {
        currentPageSections.push(section.outerHTML);
        currentHeight += sectionHeight;
      }
      if (index === sections.length - 1 && currentPageSections.length > 0)
        newSidebarPages.push([...currentPageSections]);
    });
    if (newSidebarPages.length === 0) newSidebarPages.push([]);
    setSidebarPages(newSidebarPages);
  }, [page.height, topBottomMargin, leftSectionOrder]);

  const paginateMainContent = useCallback(() => {
    if (!rightContentRef.current) return;
    const pageHeightInPx =
      parseFloat(page.height.replace("mm", "")) * 3.78;
    const availableHeight = pageHeightInPx - 2 * topBottomMargin;
    const sections = Array.from(
      rightContentRef.current.querySelectorAll(".resume-section")
    );
    let newPages = [],
      currentPageSections = [],
      currentHeight = 0;
    sections.forEach((section, index) => {
      const sectionHeight = section.offsetHeight;
      if (currentHeight + sectionHeight > availableHeight) {
        if (currentPageSections.length > 0)
          newPages.push([...currentPageSections]);
        currentPageSections = [section.outerHTML];
        currentHeight = sectionHeight;
      } else {
        currentPageSections.push(section.outerHTML);
        currentHeight += sectionHeight;
      }
      if (index === sections.length - 1 && currentPageSections.length > 0)
        newPages.push([...currentPageSections]);
    });
    if (newPages.length === 0) newPages.push([]);
    setPages(newPages);
  }, [page.height, topBottomMargin, rightSectionOrder]);

  useEffect(() => {
    const id = setTimeout(() => {
      paginateSidebarContent();
      paginateMainContent();
    }, 500);
    return () => clearTimeout(id);
  }, [
    paginateSidebarContent,
    paginateMainContent,
    educationEntries,
    savedSkills,
    savedSummary,
    workExperiences,
    savedAdditionalInfo,
    savedLanguages,
    savedAccomplishments,
    savedCertifications,
    savedReferences,
    softwareSkills,
    savedVolunteering,
    savedInterests,
    savedWebsites,
    customSections,
    leftSectionOrder,
    rightSectionOrder,
    font,
    fontSize,
    fontStyle,
    headingSize,
    sectionSpacing,
    paragraphSpacing,
    lineSpacing,
    paragraphIndent,
    lineWeight,
    themeColor,
  ]);

  const totalPages = Math.max(sidebarPages.length, pages.length, 1);

  const sidebarBoxSx = {
    width: "30%",
    minWidth: 0,
    maxWidth: "30%",
    bgcolor: themeColor.primary,
    color: "black",
    p: 3,
    display: "flex",
    flexDirection: "column",
    flexShrink: 0,
    fontFamily: font,
    fontSize: fontSize,
    fontStyle: fontStyle,
    lineHeight: `${lineSpacing}px`,
    overflow: "hidden",
    boxSizing: "border-box",
    "@media print": {
      bgcolor: themeColor.primary,
      color: "black",
      p: 2,
      width: "30%",
      maxWidth: "30%",
      display: "inline-block",
      verticalAlign: "top",
      WebkitPrintColorAdjust: "exact",
      printColorAdjust: "exact",
      overflow: "hidden",
    },
  };

  const renderSidebar = (pageIndex) => (
    <Box sx={sidebarBoxSx}>
      {sidebarPages[pageIndex] ? (
        <div
          style={{
            ...globalFontStyle,
            overflow: "hidden",
            wordBreak: "break-word",
            overflowWrap: "break-word",
            maxWidth: "100%",
          }}
          dangerouslySetInnerHTML={{
            __html: sidebarPages[pageIndex].join(""),
          }}
        />
      ) : (
        <Typography sx={getTextStyle()}>Loading...</Typography>
      )}
    </Box>
  );

  return (
    <Box sx={{ width: "100%", position: "relative", m: 5 }}>
      {/* Navigation & Controls */}
      {!exporting && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            mb: 2,
            gap: 2,
            "@media print": { display: "none" },
          }}
        >
          <IconButton
            onClick={() => setCurrentPage((p) => Math.max(0, p - 1))}
            disabled={currentPage === 0}
            color="primary"
          >
            <ChevronLeft />
          </IconButton>
          <Typography sx={{ mx: 2 }}>
            Page {currentPage + 1} of {totalPages}
          </Typography>
          <IconButton
            onClick={() =>
              setCurrentPage((p) => Math.min(totalPages - 1, p + 1))
            }
            disabled={currentPage === totalPages - 1}
            color="primary"
          >
            <ChevronRight />
          </IconButton>
          {enableDragDrop && (
            <Box sx={{ display: "flex", gap: 2, alignItems: "center", ml: 2 }}>
              <Button
                onClick={() => setArrangeDialogOpen(true)}
                variant="contained"
                startIcon={<Reorder />}
                color="primary"
              >
                Arrange Sections
              </Button>
              <Button
                onClick={resetAllSectionOrder}
                variant="outlined"
                color="secondary"
                size="small"
              >
                Reset All
              </Button>
            </Box>
          )}
        </Box>
      )}

      {/* Hidden measurement containers */}
      <Box
        ref={sidebarContentRef}
        sx={{
          visibility: "hidden",
          position: "absolute",
          top: -9999,
          width: "30%",
          overflow: "hidden",
          ...globalFontStyle,
        }}
      >
        {renderSidebarSections()}
      </Box>
      <Box
        ref={rightContentRef}
        sx={{
          visibility: "hidden",
          position: "absolute",
          top: -9999,
          width: "70%",
          overflow: "hidden",
          ...globalFontStyle,
        }}
      >
        {renderRightContentSections()}
      </Box>

      {/* Resume Pages */}
      <Box id="resume-container">
        {Array.from({ length: totalPages }).map((_, pageIndex) => (
          <Box
            key={pageIndex}
            className="resume-page"
            sx={{
              width: page.width,
              minHeight: page.height,
              mx: "auto",
              bgcolor: "#fff",
              boxShadow: pageIndex === currentPage ? 3 : 0,
              fontFamily: font,
              fontSize: fontSize,
              fontStyle: fontStyle,
              lineHeight: `${lineSpacing}px`,
              display: exporting
                ? "flex"
                : pageIndex === currentPage
                ? "flex"
                : "none",
              flexDirection: "column",
              pageBreakAfter: "always",
              overflow: "hidden",
              boxSizing: "border-box",
              "@media print": {
                display: "flex",
                width: page.width,
                height: "auto",
                minHeight: page.height,
                boxShadow: "none",
                m: 0,
              },
            }}
          >
            <Box
              sx={{
                display: "flex",
                height: "100%",
                p: `${topBottomMargin}px ${sideMargins}px`,
                flex: 1,
                overflow: "hidden",
                boxSizing: "border-box",
                minWidth: 0,
              }}
            >
              {/* Left Sidebar */}
              {sidebarPages[pageIndex] ? (
                renderSidebar(pageIndex)
              ) : (
                <Box
                  sx={{
                    width: "30%",
                    maxWidth: "30%",
                    flexShrink: 0,
                    bgcolor: themeColor.primary,
                    p: 3,
                    boxSizing: "border-box",
                  }}
                />
              )}

              {/* Divider */}
              {sidebarPages[pageIndex] && pages[pageIndex] && (
                <Divider
                  orientation="vertical"
                  flexItem
                  sx={{
                    mx: 2,
                    borderRightWidth: lineWeight,
                    flexShrink: 0,
                  }}
                />
              )}

              {/* Right Content */}
              <Box
                sx={{
                  flex: 1,
                  minWidth: 0,
                  width: sidebarPages[pageIndex] ? "70%" : "100%",
                  pl: sidebarPages[pageIndex] ? 3 : 0,
                  fontFamily: font,
                  fontSize: fontSize,
                  fontStyle: fontStyle,
                  lineHeight: `${lineSpacing}px`,
                  overflow: "hidden",
                  boxSizing: "border-box",
                  "@media print": {
                    display: "inline-block",
                    verticalAlign: "top",
                    overflow: "hidden",
                  },
                }}
              >
                {pages[pageIndex] ? (
                  <div
                    style={{
                      ...globalFontStyle,
                      overflow: "hidden",
                      wordBreak: "break-word",
                      overflowWrap: "break-word",
                      maxWidth: "100%",
                    }}
                    dangerouslySetInnerHTML={{
                      __html: pages[pageIndex].join(""),
                    }}
                  />
                ) : (
                  <Typography sx={getTextStyle()}>
                    No content for this page
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
        ))}
      </Box>

      {/* Arrange Sections Dialog */}
      <Dialog
        open={arrangeDialogOpen}
        onClose={() => setArrangeDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <Reorder sx={{ mr: 1 }} />
            Arrange Resume Sections
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
            Drag and drop sections to reorder or move between left sidebar and
            right content.
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              gap: 3,
              width: "100%",
            }}
          >
            <Box sx={{ flex: 1, width: "50%" }}>
              {renderDragPanel("left", leftSectionOrder)}
            </Box>
            <Box sx={{ flex: 1, width: "50%" }}>
              {renderDragPanel("right", rightSectionOrder)}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={resetAllSectionOrder} color="secondary">
            Reset All
          </Button>
          <Button
            onClick={() => setArrangeDialogOpen(false)}
            variant="contained"
          >
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ResumeTemplate;